// src/index.tsx
import React from 'react';
import { createRoot } from 'react-dom/client';
import { AuthProvider } from 'react-oidc-context';
import { User, WebStorageStateStore } from 'oidc-client-ts'; // Import for better typing
import { env } from './config/env';
import App from './App';

// Import styles
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './styles/index.css';
import './styles/main.css';

// Enable OIDC debug logging in development
if (import.meta.env.DEV) {
  console.log('Development mode: Enabling OIDC debugging');
  window.localStorage.setItem('oidc.debug', 'true');
}

// Configure auth with improved settings
const authConfig = {
  authority: env.auth.authority,
  client_id: env.auth.clientId,
  redirect_uri: env.auth.redirectUri,
  response_type: env.auth.responseType,
  scope: env.auth.scope,
  post_logout_redirect_uri: env.auth.logoutUri,
  
  // Improved settings
  loadUserInfo: true,
  automaticSilentRenew: true,
  monitorSession: true,
  
  // Explicitly configure storage
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  
  // Important timeouts
  staleStateAge: 3600, // 1 hour
  clockSkewInSeconds: 300, // 5 minutes tolerance for server time differences
  
  // Debug callbacks
  onSigninCallback: (user?: User) => {
    console.log('Auth callback executed', { 
      isAuthenticated: !!user,
      userProfile: user?.profile
    });
    
    // Clean up URL after successful authentication
    window.history.replaceState(
      {},
      document.title,
      window.location.pathname
    );
  },
  
  onSignoutCallback: () => {
    console.log('Signout callback executed');
    // Clean up and redirect to logout page
    sessionStorage.clear();
    localStorage.removeItem('oidc.user:' + env.auth.authority + ':' + env.auth.clientId);
    
    window.location.href = env.auth.logoutUri || '/logout';
  },
};

// Add global error handler
window.addEventListener('error', (event) => {
  console.error('Application error:', event.error);
});

// Get root element
const container = document.getElementById('root');

// Check if container exists to avoid TypeScript errors
if (container) {
  const root = createRoot(container);

  root.render(
    <React.StrictMode>
      <AuthProvider {...authConfig}>
        <App />
      </AuthProvider>
    </React.StrictMode>
  );
} else {
  console.error('Root element not found');
}